import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { LucideAngularModule } from 'lucide-angular';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ExcelImportService } from '../../services/excel-import.service';
import { TripService } from '../../services/trip.service';
import { TripCreationModalService } from '../../services/trip-creation-modal.service';
import { ExcelImportMode, ExcelImportResult } from '../../models/excel-import';

/**
 * Excel mass-import POC screen. Presents the two separate flows (A: fill our
 * template, B: upload any file) and shows a per-row result report so the
 * business team can compare what works best for clients.
 */
@Component({
  selector: 'app-excel-import',
  imports: [CommonModule, TranslocoModule, ButtonModule, LucideAngularModule],
  templateUrl: './excel-import.component.html',
})
export class ExcelImportComponent {
  private excelImportService = inject(ExcelImportService);
  private tripService = inject(TripService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  tripCreationModalService = inject(TripCreationModalService);

  importing = signal(false);
  downloading = signal(false);
  result = signal<ExcelImportResult | null>(null);

  downloadTemplate(): void {
    this.downloading.set(true);
    this.excelImportService.downloadTemplate().subscribe({
      next: (response) => {
        this.downloading.set(false);
        if (!response.body) return;
        const blob = new Blob([response.body], {
          type:
            response.body.type ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const match = response.headers
          .get('Content-Disposition')
          ?.match(/filename="(.+)"/);
        const fileName = match
          ? match[1]
          : 'act4travel-trip-import-template.xlsx';
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.downloading.set(false);
        this.notificationService.error(
          this.translocoService.translate('calculator.excelImportFailed'),
        );
      },
    });
  }

  onFileSelected(event: Event, mode: ExcelImportMode): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) this.runImport(mode, file);
  }

  viewTrip(tripId: string): void {
    this.tripCreationModalService.close();
    this.tripService.navigateToTripDashboard(tripId);
  }

  reset(): void {
    this.result.set(null);
  }

  private async runImport(mode: ExcelImportMode, file: File): Promise<void> {
    this.importing.set(true);
    this.result.set(null);
    try {
      const result = await firstValueFrom(
        this.excelImportService.import(mode, file),
      );
      this.result.set(result);
    } catch {
      this.notificationService.error(
        this.translocoService.translate('calculator.excelImportFailed'),
      );
    } finally {
      this.importing.set(false);
    }
  }
}
